import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { api } from '../lib/api';
import { useT } from '../lib/i18n';
import { holdModal } from '../lib/modal';
import { Layer } from './Layer';
import { AccountIcon, GalleryIcon, ModelsIcon, RollIcon, SearchIcon, ShootIcon } from './Icons';

/**
 * The search bar, and the palette behind it.
 *
 * Every screen in this studio is a list of named things — faces, rolls,
 * frames — and up to now the only way to a named thing was to remember which
 * of five sections holds it and then read down the page. The bar is the other
 * way: type three letters of a model's name and press Enter.
 *
 * It is one control doing two jobs, which is why it is one component: the bar
 * is what you see on every page, and the palette is what the bar opens. The
 * strip the bar sits in belongs to the shell, which puts the button that
 * starts a new roll beside it — so this renders the control, not the row.
 */

type Hit = {
  id: string;
  group: 'section' | 'talent' | 'roll';
  label: string;
  hint?: string;
  to: string;
  icon: ReactNode;
};

/** What the palette knows about, loaded the first time it is opened. */
type Catalogue = { talent: Hit[]; rolls: Hit[] };

const EMPTY: Catalogue = { talent: [], rolls: [] };

/** Case- and accent-insensitive, so "eloise" finds "Éloïse". */
const fold = (text: string): string => text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

export function Palette() {
  const t = useT();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const [catalogue, setCatalogue] = useState<Catalogue>(EMPTY);
  const asked = useRef(false);
  const field = useRef<HTMLInputElement>(null);

  const sections = useMemo<Hit[]>(
    () => [
      { id: 's-new', group: 'section', label: t('app.nav.shoot'), to: '/new', icon: <ShootIcon /> },
      { id: 's-talent', group: 'section', label: t('app.nav.talent'), to: '/models', icon: <ModelsIcon /> },
      { id: 's-rolls', group: 'section', label: t('app.nav.rolls'), to: '/shoots', icon: <RollIcon /> },
      { id: 's-sheet', group: 'section', label: t('app.nav.sheet'), to: '/gallery', icon: <GalleryIcon /> },
      {
        id: 's-settings',
        group: 'section',
        label: t('app.nav.account'),
        to: '/settings',
        icon: <AccountIcon />,
      },
    ],
    [t],
  );

  /*
   * Nothing is fetched until the palette is first opened: the bar is on every
   * screen, and a studio with four hundred frames should not pay for a list of
   * them on every page load to render a placeholder.
   */
  useEffect(() => {
    if (!open || asked.current) return;
    asked.current = true;
    void Promise.all([api.listModels(), api.listShoots()])
      .then(([models, shoots]) =>
        setCatalogue({
          talent: models.map((model) => ({
            id: `m-${model.id}`,
            group: 'talent',
            label: model.name,
            to: '/models',
            icon: <ModelsIcon />,
          })),
          rolls: shoots.map((shoot) => ({
            id: `r-${shoot.id}`,
            group: 'roll',
            label: shoot.name,
            hint: `${shoot.shotCount} ${t(shoot.shotCount === 1 ? 'common.frame' : 'common.frames')}`,
            to: `/shoots/${shoot.id}`,
            icon: <RollIcon />,
          })),
        }),
      )
      .catch(() => undefined);
  }, [open, t]);

  const hits = useMemo<Hit[]>(() => {
    const needle = fold(query.trim());
    /* Nothing typed yet: offer the sections and the last few rolls, so the
       palette is a way to move as well as a way to find. */
    if (!needle) return [...sections, ...catalogue.rolls.slice(0, 5)];
    const matches = (hit: Hit) => fold(hit.label).includes(needle);
    return [
      ...sections.filter(matches),
      ...catalogue.talent.filter(matches).slice(0, 8),
      ...catalogue.rolls.filter(matches).slice(0, 8),
    ];
  }, [query, sections, catalogue]);

  const shut = useCallback(() => {
    setOpen(false);
    setQuery('');
    setCursor(0);
  }, []);

  const choose = useCallback(
    (hit: Hit | undefined) => {
      if (!hit) return;
      shut();
      navigate(hit.to);
    },
    [navigate, shut],
  );

  /* The shortcut the chip on the bar promises. Bound to the window, because
     the point of it is that you have not got your hands near the bar. */
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen((was) => !was);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (!open) return;
    field.current?.focus();
    return holdModal();
  }, [open]);

  // A shorter list than the one the cursor was on must not leave it past the end.
  useEffect(() => setCursor((was) => (was >= hits.length ? 0 : was)), [hits.length]);

  const mac = typeof navigator !== 'undefined' && /Mac|iP(hone|ad|od)/.test(navigator.userAgent);

  return (
    <>
      <button type="button" className="searchbar" onClick={() => setOpen(true)}>
        <SearchIcon />
        <span className="searchbar__hint">{t('search.placeholder')}</span>
        <kbd className="searchbar__keys">{mac ? '⌘ K' : 'Ctrl K'}</kbd>
      </button>

      {open && (
        <Layer>
          <div className="palette" role="presentation">
            <button
              type="button"
              className="palette__scrim"
              aria-label={t('common.close')}
              tabIndex={-1}
              onClick={shut}
            />
            <div className="palette__card" role="dialog" aria-modal="true" aria-label={t('search.title')}>
              <div className="palette__head">
                <SearchIcon />
                <input
                  ref={field}
                  className="palette__field"
                  value={query}
                  role="combobox"
                  aria-expanded="true"
                  aria-controls="palette-list"
                  aria-label={t('search.title')}
                  placeholder={t('search.placeholder')}
                  onChange={(event) => {
                    setQuery(event.target.value);
                    setCursor(0);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Escape') shut();
                    if (event.key === 'ArrowDown') {
                      event.preventDefault();
                      setCursor((was) => (hits.length ? (was + 1) % hits.length : 0));
                    }
                    if (event.key === 'ArrowUp') {
                      event.preventDefault();
                      setCursor((was) => (hits.length ? (was - 1 + hits.length) % hits.length : 0));
                    }
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      choose(hits[cursor]);
                    }
                  }}
                />
                <kbd className="searchbar__keys">esc</kbd>
              </div>

              <div className="palette__list" id="palette-list" role="listbox" aria-label={t('search.title')}>
                {hits.length === 0 ? (
                  <p className="palette__none">{t('search.none', { query: query.trim() })}</p>
                ) : (
                  hits.map((hit, index) => (
                    <Row
                      key={hit.id}
                      hit={hit}
                      heading={
                        index === 0 || hits[index - 1]!.group !== hit.group ? t(HEADING[hit.group]) : ''
                      }
                      on={index === cursor}
                      onPick={() => choose(hit)}
                      onOver={() => setCursor(index)}
                    />
                  ))
                )}
              </div>
            </div>
          </div>
        </Layer>
      )}
    </>
  );
}

const HEADING: Record<Hit['group'], string> = {
  section: 'search.group.sections',
  talent: 'search.group.talent',
  roll: 'search.group.rolls',
};

function Row({
  hit,
  heading,
  on,
  onPick,
  onOver,
}: {
  hit: Hit;
  heading: string;
  on: boolean;
  onPick: () => void;
  onOver: () => void;
}) {
  return (
    <>
      {heading && <span className="palette__heading">{heading}</span>}
      <button
        type="button"
        role="option"
        aria-selected={on}
        className={`palette__row ${on ? 'is-on' : ''}`}
        onMouseMove={onOver}
        onClick={onPick}
      >
        <span className="palette__icon">{hit.icon}</span>
        <span className="palette__label">{hit.label}</span>
        {hit.hint && <span className="palette__meta">{hit.hint}</span>}
      </button>
    </>
  );
}
